import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import stakeholderService from '../services/stakeholderService';
import { ensureBuiltinStakeholderCategories } from '../utils/stakeholderCategories';

const useAtelier3Store = create(
  subscribeWithSelector((set, get) => ({
    stakeholders: [],
    stakeholderThresholds: null,
    isDataLoaded: false,
    isLoading: false,
    error: null,
    analysisId: null,

    loadStakeholders: async (analysisId) => {
      if (!analysisId) return { success: false, data: [] };
      set({ analysisId });
      try {
        const response = await stakeholderService.getStakeholders(analysisId);
        if (response.success) {
          if (analysisId !== get().analysisId) return;
          set({ stakeholders: ensureBuiltinStakeholderCategories(response.data), isDataLoaded: true });
          return response;
        }
        return { success: true, data: [] };
      } catch (error) {
        console.error('[useAtelier3Store] Error loading stakeholders:', error);
        return { success: false, data: [] };
      }
    },

    saveStakeholders: async (analysisId, stakeholdersData, radarImageData = null, onBroadcast) => {
      try {
        const response = await stakeholderService.saveStakeholders(analysisId, stakeholdersData, radarImageData);
        if (response.success) {
          set({ stakeholders: ensureBuiltinStakeholderCategories(response.data ?? stakeholdersData) });
          if (onBroadcast) {
            onBroadcast('atelier3-activite1');
          }
        }
        return response;
      } catch (error) {
        console.error('[useAtelier3Store] Error saving stakeholders:', error);
        return { success: false, message: error.message || 'Save failed', data: stakeholdersData };
      }
    },

    refreshSlice: async (analysisId, tab) => {
      const sliceMap = {
        'atelier3-activite1': async () => {
          const r = await stakeholderService.getStakeholders(analysisId);
          if (r.success) set({ stakeholders: ensureBuiltinStakeholderCategories(r.data) });
        },
        'atelier3-activite2': async () => {
          const r = await stakeholderService.getStakeholders(analysisId);
          if (r.success) set({ stakeholders: ensureBuiltinStakeholderCategories(r.data) });
        },
        'atelier3-activite3': async () => {
          set({ stakeholderThresholds: null });
          const r = await stakeholderService.getThresholds(analysisId);
          if (r.success) set({ stakeholderThresholds: r.data });
        },
      };
      const fn = sliceMap[tab];
      if (fn) {
        try { await fn(); }
        catch (e) { console.error(`[A3Store] refreshSlice(${tab}) failed:`, e); }
      }
    },

    getStakeholderThresholds: async (analysisId, forceRefresh = false) => {
      try {
        const { stakeholderThresholds } = get();
        if (stakeholderThresholds && !forceRefresh) {
          return { success: true, data: stakeholderThresholds };
        }
        const response = await stakeholderService.getThresholds(analysisId);
        if (response.success) {
          set({ stakeholderThresholds: response.data });
          return response;
        }
        const defaultThresholds = { danger: 3, control: 1.5, watch: 0.5 };
        set({ stakeholderThresholds: defaultThresholds });
        return { success: true, data: defaultThresholds };
      } catch (error) {
        console.error('[useAtelier3Store] Error getting stakeholder thresholds:', error);
        const defaultThresholds = { danger: 3, control: 1.5, watch: 0.5 };
        return { success: true, data: defaultThresholds };
      }
    },

    getEbiosensSuggestedThresholds: () => {
      const { stakeholders } = get();
      if (!stakeholders || stakeholders.length === 0) return null;

      const levels = stakeholders
        .map(s => s.threatLevel)
        .filter(tl => tl != null && !isNaN(tl) && isFinite(tl))
        .sort((a, b) => a - b);

      if (levels.length === 0) return null;

      const n = levels.length;
      const pct = (p) => levels[Math.max(0, Math.min(n - 1, Math.round(p * (n - 1))))];

      return {
        danger:  pct(0.90),
        control: pct(0.50),
        watch:   pct(0.10),
      };
    },

    updateStakeholderThresholds: async (analysisId, thresholds, onBroadcast) => {
      try {
        const response = await stakeholderService.updateThresholds(analysisId, thresholds);
        if (response.success) {
          set({ stakeholderThresholds: thresholds });
          if (onBroadcast) {
            onBroadcast('atelier3-activite2');
          }
        }
        return response;
      } catch (error) {
        console.error('[useAtelier3Store] Error updating stakeholder thresholds:', error);
        return { success: false, message: error.message || 'Update failed' };
      }
    },

    getStakeholderRadarImage: async (analysisId) => {
      try {
        const response = await stakeholderService.getStakeholderRadarImage(analysisId);
        return response;
      } catch (error) {
        console.error('[useAtelier3Store] Error getting stakeholder radar image:', error);
        return { success: false, message: 'Error retrieving radar image', error: error.message };
      }
    },

    resetState: () => {
      set({
        stakeholders: [],
        stakeholderThresholds: null,
        isDataLoaded: false,
        isLoading: false,
        error: null,
        analysisId: null,
      });
    },
  }))
);

export default useAtelier3Store;